<script setup lang="ts">
import type { ComponentRowData } from "~~/types/shipexkurs";

const props = withDefaults(defineProps<{ row: ComponentRowData; depth?: number }>(), { depth: 0 });

const icon = computed(() => getShipexkursComponentIcon(props.row.type, props.row.subType));

/**
 * Farbe der Ressourcen-Stats-Icons hängt an der Icon-BEDEUTUNG (Energie vs.
 * Kühlung), nicht an der Position (Headline vs. Badge) — bei Kühlern ist die
 * Headline z.B. das Kühler-Icon selbst, das dann fälschlich in der
 * Energie-Farbe eingefärbt würde.
 */
function resourceIconClass(iconName: string): string {
  if (iconName === 'powerplant') return 'text-warning';
  if (iconName === 'cooler') return 'text-aris-400';
  return 'text-aris-400';
}
</script>

<template>
  <div :class="depth ? 'ml-4 border-l-2 border-l-primary-400/40 pl-3' : ''">
    <UCard
      variant="outline"
      class="hover:bg-(--ui-bg)/40 duration-200 ring-0 border border-default transition"
      :class="depth ? 'hover:border-y-aris-400/33 hover:border-r-aris-400/33' : 'hover:border-aris-400/33'"
      :ui="{body: 'p-0 sm:p-0' }"
    >
      <template #default>
        <div class="flex flex-wrap py-2 px-3 gap-y-2 gap-x-3 text-sm items-center">
          <div v-if="row.size != null" class="text-white border border-accented rounded bg-elevated px-2 py-1 whitespace-nowrap">S{{ row.size }}</div>
          <ShipexkursSystemIcon v-if="icon" :name="icon" class="size-5 shrink-0 text-aris-400" />
          <!-- Component base details -->
          <div class="flex-col gap-px flex overflow-hidden">
            <div class="flex flex-wrap grow gap-x-1 mr-1 items-center wrap-break-word">
              {{row.name}}
              <span v-if="row.count > 1" class="rounded bg-elevated px-1.5 text-xs text-neutral-300">{{ row.count }}×</span>
              <div
                v-if="(row.class || row.grade) && !row.type?.toLowerCase()?.includes('weapon') && !row.type?.toLowerCase()?.includes('missile') && !row.type?.toLowerCase()?.includes('armor') && !row.type?.toLowerCase()?.includes('turret')"
                class="text-[.8rem] text-dimmed whitespace-nowrap"
              >
                <template v-if="row.class">{{ row.class }}</template>
                <span v-if="row.class && row.grade"> · </span>
                <template v-if="row.grade">Qualität {{ row.grade }}</template>
              </div>
            </div>
            <div v-if="row.manufacturer" class="flex items-center gap-1 text-xs text-dimmed whitespace-nowrap">
              <NuxtImg
                v-if="row.manufacturerLogo"
                :src="row.manufacturerLogo"
                :alt="row.manufacturer"
                class="my-0! size-3.5 shrink-0 object-contain"
              />
              {{row.manufacturer}}
            </div>
          </div>
          <!-- Component headline (Rows ohne Ressourcen-Badges, z.B. Waffen/Schilde) -->
          <div v-if="row.headline && !row.badges?.length" class="ml-auto text-right">
            <span class="text-warning text-[.9rem] font-bold block">{{row.headline.value}}</span>
            <span class="text-dimmed text-xs block">{{row.headline.unit}}</span>
          </div>
          <!-- Ressourcen-Stats-Zeile (Energie/Kühlbedarf bei Power Plants & Kühlern) — eigene
          Zeile unter dem Namen statt neben der Headline, damit Icon+Zahl-Paare gleichwertig
          und gut lesbar nebeneinander stehen. -->
          <div v-if="row.badges?.length" class="flex basis-full flex-wrap items-center gap-4">
            <span v-if="row.headline" class="flex items-center gap-1.5">
              <ShipexkursSystemIcon v-if="icon" :name="icon" class="size-4 shrink-0" :class="resourceIconClass(icon)" />
              <span class="text-base font-bold text-neutral-100">{{ row.headline.value }}</span>
            </span>
            <span
              v-for="badge in row.badges"
              :key="badge.icon"
              class="flex items-center gap-1.5"
            >
              <ShipexkursSystemIcon :name="badge.icon" class="size-4 shrink-0" :class="resourceIconClass(badge.icon)" />
              <span class="text-base font-bold text-neutral-100">{{ badge.value }}</span>
            </span>
          </div>
          <!-- Component details -->
          <div v-if="row.details?.length" class="flex basis-full flex-wrap gap-x-3 gap-y-px">
            <span v-for="item in row.details" :key="item.label" class="text-xs">
              <span class="text-dimmed">{{item.label}}: </span>
              <span class="font-medium text-neutral-300">{{ item.value }}</span>
            </span>
          </div>
        </div>
      </template>
    </UCard>
    <!-- <div -->
      <!-- class="rounded-md bg-white/[0.02] px-3 py-2 border-white/10 border hover:border-white/20 hover:bg-white/[0.04] transition duration-200" -->
      <!-- :class="depth ? 'ml-4 border-l border-white/10 pl-3' : ''" -->
    <!-- > -->

    <!-- </div> -->

    <ShipexkursComponentRowItem
      v-for="child in row.children"
      :key="child.key"
      :row="child"
      :depth="depth + 1"
      class="mt-1"
    />
  </div>
</template>
